import React from 'react';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Dumbbell,
  Minus,
  MoreVertical,
} from '../../../bravosIcons';
import type { TrainerExerciseListRow } from '../../../../../types/dataModel';
import LibraryEmptyState from '../components/LibraryEmptyState';
import { LIB_EXERCISES_PAGE_SIZE, type LibraryExercisesApi } from '../../hooks/useLibraryExercises';
import { splitTags } from '../../../../../utils/exerciseDisplayUtils';
import './exercises-tab.css';

export type ExercisesTableDesktopProps = {
  ex: LibraryExercisesApi;
  exercises: TrainerExerciseListRow[];
  profileId: string | undefined;
  trainerInitialsLabel: string;
  selectedExercises: Set<string>;
  openExerciseMenu: string | null;
  setOpenExerciseMenu: (id: string | null) => void;
  exerciseMenuRef: React.RefObject<HTMLDivElement>;
  libExercisesPageSelectionState: 'none' | 'some' | 'all';
  onToggleSelectExercisesPage: () => void;
  onToggleSelectExercise: (id: string) => void;
  onViewExercise: (row: TrainerExerciseListRow) => void;
  onEditExercise: (id: string) => void;
  onDuplicateExercise: (id: string) => void;
  onDeleteExercise: (id: string, name: string) => void;
  onBravosDeleteAttempt: () => void;
  onCreateExercise: () => void;
};

export function ExercisesTableDesktop({
  ex,
  exercises,
  profileId,
  trainerInitialsLabel,
  selectedExercises,
  openExerciseMenu,
  setOpenExerciseMenu,
  exerciseMenuRef,
  libExercisesPageSelectionState,
  onToggleSelectExercisesPage,
  onToggleSelectExercise,
  onViewExercise,
  onEditExercise,
  onDuplicateExercise,
  onDeleteExercise,
  onBravosDeleteAttempt,
  onCreateExercise,
}: ExercisesTableDesktopProps) {
  if (ex.libExercisesEmptyVariant !== null) {
    return (
      <LibraryEmptyState
        variant={ex.libExercisesEmptyVariant}
        tab="exercises"
        searchQuery={ex.exerciseNameFilter}
        hasActiveFilters={ex.libExercisesHasActiveControls}
        onClearAll={ex.handleClearAllExercises}
        onCreate={onCreateExercise}
      />
    );
  }

  return (
    <div className="lib-table-wrap">
      <table className="lib-table">
        <thead>
          <tr>
            <th className="lib-col-select" aria-label="Select column">
              <button
                type="button"
                className={`lib-checkbox ${libExercisesPageSelectionState === 'all' ? 'is-checked' : libExercisesPageSelectionState === 'some' ? 'is-indeterminate' : ''}`}
                onClick={onToggleSelectExercisesPage}
                aria-label="Select all exercises on this page"
                aria-checked={libExercisesPageSelectionState === 'all' ? true : libExercisesPageSelectionState === 'some' ? 'mixed' : false}
                role="checkbox"
              >
                {libExercisesPageSelectionState === 'all' && <Check />}
                {libExercisesPageSelectionState === 'some' && <Minus />}
              </button>
            </th>
            <th className="lib-col-index">№</th>
            <th
              className={`is-sortable ${(ex.libExerciseSort === 'name-asc' || ex.libExerciseSort === 'name-desc') ? 'is-sorted' : ''}`}
              onClick={() => ex.handleLibExerciseHeaderSort('name')}
              aria-sort={ex.libExerciseSort === 'name-asc' ? 'ascending' : ex.libExerciseSort === 'name-desc' ? 'descending' : 'none'}
            >
              <span className="lib-th-inner">
                Name
                {ex.libExerciseSort === 'name-asc'
                  ? <ChevronUp />
                  : ex.libExerciseSort === 'name-desc'
                    ? <ChevronDown />
                    : <ChevronDown style={{ opacity: 0.4 }} />}
              </span>
            </th>
            <th
              className={`is-sortable ${(ex.libExerciseSort === 'yours-first' || ex.libExerciseSort === 'yours-last') ? 'is-sorted' : ''}`}
              onClick={() => ex.handleLibExerciseHeaderSort('createdBy')}
              aria-sort={ex.libExerciseSort === 'yours-first' ? 'ascending' : ex.libExerciseSort === 'yours-last' ? 'descending' : 'none'}
            >
              <span className="lib-th-inner">
                Created by
                {ex.libExerciseSort === 'yours-first'
                  ? <ChevronUp />
                  : ex.libExerciseSort === 'yours-last'
                    ? <ChevronDown />
                    : <ChevronDown style={{ opacity: 0.4 }} />}
              </span>
            </th>
            <th>Muscle group</th>
            <th>Equipment</th>
            <th className="lib-col-actions" aria-label="Row actions" />
          </tr>
        </thead>
        <tbody>
          {ex.filteredAndSortedExercises.length === 0 ? (
            <tr>
              <td colSpan={7} className="lib-empty">
                <Dumbbell aria-hidden="true" />
                <h3>
                  {exercises.length === 0 ? 'No exercises yet' : 'No exercises match your filters'}
                </h3>
                <p>
                  {exercises.length === 0
                    ? 'Get started by creating your first exercise.'
                    : 'Try adjusting search, chips, or sort.'}
                </p>
                {exercises.length === 0 && (
                  <button type="button" className="lib-empty-cta" onClick={onCreateExercise}>
                    Create exercise
                  </button>
                )}
              </td>
            </tr>
          ) : (
            ex.libExercisesPageRows.map((row, indexOnPage) => {
              const globalIndex = (ex.libExercisesPage - 1) * LIB_EXERCISES_PAGE_SIZE + indexOnPage;
              const isSelected = selectedExercises.has(row.id);
              const isYou = row.createdBy === profileId;
              const muscleTags = splitTags(row.muscleGroup);
              const equipmentTags = splitTags(row.equipment);
              return (
                <tr
                  key={`ex-${row.id}`}
                  className={`${isSelected ? 'is-selected' : ''}`}
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (
                      target.closest('.lib-checkbox')
                      || target.closest('.lib-kebab')
                      || target.closest('.lib-kebab-menu')
                    ) {
                      return;
                    }
                    void onViewExercise(row);
                  }}
                >
                  <td className="lib-col-select" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className={`lib-checkbox ${isSelected ? 'is-checked' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleSelectExercise(row.id);
                      }}
                      aria-label={`Select row ${row.name}`}
                      aria-checked={isSelected}
                      role="checkbox"
                    >
                      {isSelected && <Check />}
                    </button>
                  </td>
                  <td className="lib-col-index">
                    <span className="lib-row-index">{String(globalIndex + 1).padStart(2, '0')}</span>
                  </td>
                  <td><div className="lib-row-name">{row.name}</div></td>
                  <td>
                    <div className="lib-author">
                      <span className={`lib-author-mark ${isYou ? 'is-you' : 'is-system'}`}>
                        {isYou ? trainerInitialsLabel : 'B'}
                      </span>
                      <span className="lib-author-name">{isYou ? 'You' : 'Bravos'}</span>
                    </div>
                  </td>
                  <td>
                    <div className="lib-tag-row">
                      {muscleTags.length === 0 ? null : (
                        <>
                          {muscleTags.slice(0, 3).map((t) => (
                            <span key={`muscle-${row.id}-${t}`} className="lib-muscle">{t}</span>
                          ))}
                          {muscleTags.length > 3 && (
                            <span className="lib-muscle lib-tag-more" title={muscleTags.slice(3).join(', ')}>
                              +{muscleTags.length - 3}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="lib-tag-row">
                      {equipmentTags.length === 0 ? null : (
                        <>
                          {equipmentTags.slice(0, 3).map((t) => (
                            <span key={`equip-${row.id}-${t}`} className="lib-tag">{t}</span>
                          ))}
                          {equipmentTags.length > 3 && (
                            <span className="lib-tag lib-tag-more" title={equipmentTags.slice(3).join(', ')}>
                              +{equipmentTags.length - 3}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td className="lib-col-actions" onClick={(e) => e.stopPropagation()}>
                    <div className="relative inline-block" ref={openExerciseMenu === row.id ? exerciseMenuRef : undefined}>
                      <button
                        type="button"
                        className={`lib-kebab${openExerciseMenu === row.id ? ' is-open' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenExerciseMenu(openExerciseMenu === row.id ? null : row.id);
                        }}
                        aria-label={`Row actions for ${row.name}`}
                        aria-haspopup="menu"
                        aria-expanded={openExerciseMenu === row.id}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {openExerciseMenu === row.id && (
                        <>
                          <div
                            className="fixed inset-0 z-[90]"
                            onClick={(e) => { e.stopPropagation(); setOpenExerciseMenu(null); }}
                            onTouchStart={(e) => e.stopPropagation()}
                            style={{ touchAction: 'none' }}
                          />
                          <div className="lib-kebab-menu" role="menu" onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                            <button type="button" role="menuitem" className="lib-menu-item" onClick={(e) => { e.stopPropagation(); setOpenExerciseMenu(null); void onEditExercise(row.id); }}>Edit</button>
                            <button type="button" role="menuitem" className="lib-menu-item" onClick={(e) => { e.stopPropagation(); setOpenExerciseMenu(null); void onDuplicateExercise(row.id); }}>Duplicate</button>
                            <div className="lib-kebab-menu-divider" role="separator" />
                            <button type="button" role="menuitem" className="lib-menu-item lib-menu-item--danger" onClick={(e) => {
                              e.stopPropagation();
                              setOpenExerciseMenu(null);
                              if (isYou) void onDeleteExercise(row.id, row.name);
                              else onBravosDeleteAttempt();
                            }}>Delete</button>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {ex.filteredAndSortedExercises.length > LIB_EXERCISES_PAGE_SIZE && (
        <div className="lib-table-foot">
          <div className="lib-foot-meta">
            Showing <strong className="lib-tnum">{ex.libExerciseRangeStart}–{ex.libExerciseRangeEnd}</strong> of <strong className="lib-tnum">{ex.filteredAndSortedExercises.length}</strong>
          </div>
          <div className="lib-pager" role="navigation" aria-label="Pagination">
            <button type="button" className="lib-page-btn" onClick={() => ex.setLibExercisesPage((p) => Math.max(1, p - 1))} disabled={ex.libExercisesPage === 1} aria-label="Previous page">
              <ChevronLeft className="w-4 h-4" />
            </button>
            {ex.libBuildPageWindow(ex.libExercisesPage, ex.libExercisesTotalPages).map((p, i) =>
              p === 'ellipsis' ? (
                <span key={`ex-ellipsis-${i}`} className="lib-page-ellipsis" aria-hidden="true">…</span>
              ) : (
                <button key={`ex-page-${p}`} type="button" className={`lib-page-btn ${p === ex.libExercisesPage ? 'is-active' : ''}`} onClick={() => ex.setLibExercisesPage(p)} aria-label={`Go to page ${p}`} aria-current={p === ex.libExercisesPage ? 'page' : undefined}>{p}</button>
              )
            )}
            <button type="button" className="lib-page-btn" onClick={() => ex.setLibExercisesPage((p) => Math.min(ex.libExercisesTotalPages, p + 1))} disabled={ex.libExercisesPage === ex.libExercisesTotalPages} aria-label="Next page">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
